'use client';

import { useRunnerRequestUiRuntime } from '../runtime';

import { useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { type JSONContent } from '@tiptap/core';
import { type PromptPreset } from '../lib/api';
import { isApiError } from '../lib/apiError';
import { getPlainText } from './daemonTriggerDetailUtils';
import { BaseModal } from './BaseModal';
import { Select, type SelectOption } from './Select';
import { TiptapEditor } from './TiptapEditor';
import { UserConventionSelector, type UserConventionOption } from './UserConventionSelector';

type PromptPresetModalProps = {
  isOpen: boolean;
  onClose: () => void;
  token: string | null;
  projectId: string;
  memberId: string | null;
  onSelectPreset?: (prompt: JSONContent, userConventionIds: string[], planMode: boolean) => void;
  currentPrompt?: JSONContent;
  currentUserConventionIds?: string[];
  userConventionOptions?: UserConventionOption[];
  projectOptions?: SelectOption[];
  onProjectChange?: (projectId: string) => void;
  /**
   * 프리셋 쓰기 가능 여부(호스트가 `promptPresets.write` 보유 여부로 판단해 넘긴다).
   * false면 저장·편집·삭제 UI를 그리지 않는다. 보안 경계는 서버가 맡고, 이 값은 안내용이다.
   */
  canWrite?: boolean;
};

export const PromptPresetModal = ({
  isOpen,
  onClose,
  token,
  projectId,
  memberId,
  onSelectPreset,
  currentPrompt,
  currentUserConventionIds = [],
  userConventionOptions = [],
  projectOptions,
  onProjectChange,
  canWrite = true,
}: PromptPresetModalProps) => {
  const {
    usePromptPresets,
    useCreatePromptPresetMutation,
    useUpdatePromptPresetMutation,
    useDeletePromptPresetMutation,
  } = useRunnerRequestUiRuntime();
  const t = useTranslations('components.appLayout');
  const tCommon = useTranslations('common');
  const { data: presets, error: presetsError, isError: isPresetsError } = usePromptPresets(token, projectId);
  const presetsLoadFailed = isPresetsError;
  const presetsReadDenied = presetsLoadFailed && isApiError(presetsError) && presetsError.statusCode === 403;
  const createMutation = useCreatePromptPresetMutation(token, projectId);
  const updateMutation = useUpdatePromptPresetMutation(token, projectId);
  const deleteMutation = useDeletePromptPresetMutation(token, projectId);

  const [search, setSearch] = useState('');
  const [isSaveOpen, setIsSaveOpen] = useState(false);
  const [savePresetName, setSavePresetName] = useState('');
  const [saveUserConventionIds, setSaveUserConventionIds] = useState<string[]>(currentUserConventionIds);
  const [saveIsShared, setSaveIsShared] = useState(false);
  const [savePlanMode, setSavePlanMode] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [editingPrompt, setEditingPrompt] = useState<JSONContent | null>(null);
  const [editingUserConventionIds, setEditingUserConventionIds] = useState<string[]>([]);
  const [editingIsShared, setEditingIsShared] = useState(false);
  const [editingPlanMode, setEditingPlanMode] = useState(false);

  const hasPromptContent =
    currentPrompt && JSON.stringify(currentPrompt) !== '{"type":"doc","content":[{"type":"paragraph"}]}';

  const filtered = useMemo(() => {
    if (!presets) return { mine: [], shared: [] };
    const q = search.toLowerCase().trim();
    const list = q ? presets.filter((p) => p.name.toLowerCase().includes(q)) : presets;
    return {
      mine: list.filter((p) => p.createdByMemberId === memberId),
      shared: list.filter((p) => p.isShared && p.createdByMemberId !== memberId),
    };
  }, [presets, search, memberId]);

  const isOwner = (preset: PromptPreset) => preset.createdByMemberId === memberId;

  const resetSaveForm = () => {
    setSavePresetName('');
    setSaveUserConventionIds(currentUserConventionIds);
    setSaveIsShared(false);
    setSavePlanMode(false);
  };

  const handleSelect = (preset: PromptPreset) => {
    if (!onSelectPreset) return;
    onSelectPreset(preset.prompt as JSONContent, preset.userConventionIds, preset.planMode);
    onClose();
  };

  const handleSave = async () => {
    if (!savePresetName.trim() || !currentPrompt) return;
    try {
      await createMutation.mutateAsync({
        name: savePresetName.trim(),
        prompt: currentPrompt,
        userConventionIds: saveUserConventionIds,
        planMode: savePlanMode,
        isShared: saveIsShared,
      });
      toast.success(t('promptPresetSaved'));
      resetSaveForm();
      setIsSaveOpen(false);
    } catch {
      toast.error(t('promptPresetSaveFailed'));
    }
  };

  const handleSaveUserConventionToggle = (conventionId: string, checked: boolean) => {
    setSaveUserConventionIds((prev) => {
      if (checked) {
        if (prev.includes(conventionId)) return prev;
        return [...prev, conventionId];
      }
      return prev.filter((id) => id !== conventionId);
    });
  };

  const handleEditStart = (preset: PromptPreset) => {
    setEditingId(preset.id);
    setEditingName(preset.name);
    setEditingPrompt(preset.prompt as JSONContent);
    setEditingUserConventionIds(preset.userConventionIds);
    setEditingIsShared(preset.isShared);
    setEditingPlanMode(preset.planMode);
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditingName('');
    setEditingPrompt(null);
    setEditingUserConventionIds([]);
    setEditingIsShared(false);
    setEditingPlanMode(false);
  };

  const handleEditingUserConventionToggle = (conventionId: string, checked: boolean) => {
    setEditingUserConventionIds((prev) => {
      if (checked) {
        if (prev.includes(conventionId)) return prev;
        return [...prev, conventionId];
      }
      return prev.filter((id) => id !== conventionId);
    });
  };

  const handleEditSubmit = async (presetId: string) => {
    const trimmed = editingName.trim();
    if (!trimmed) {
      handleEditCancel();
      return;
    }
    try {
      await updateMutation.mutateAsync({
        id: presetId,
        input: {
          name: trimmed,
          prompt: editingPrompt ?? undefined,
          userConventionIds: editingUserConventionIds,
          planMode: editingPlanMode,
          isShared: editingIsShared,
        },
      });
      toast.success(t('promptPresetUpdated'));
    } catch {
      toast.error(t('promptPresetUpdateFailed'));
    }
    handleEditCancel();
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteMutation.mutateAsync(id);
      toast.success(t('promptPresetDeleted'));
    } catch {
      toast.error(t('promptPresetDeleteFailed'));
    }
  };

  const renderPresetRow = (preset: PromptPreset) => {
    const owner = isOwner(preset);
    const isEditing = editingId === preset.id;

    if (isEditing) {
      return (
        <div key={preset.id} className="rounded-xl border border-primary/30 bg-base-100 p-3 space-y-2">
          <input
            type="text"
            className="input input-bordered input-sm w-full"
            value={editingName}
            onChange={(e) => setEditingName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') handleEditCancel();
            }}
            maxLength={100}
            autoFocus
          />
          <div className="max-h-24 overflow-y-auto rounded-lg border border-base-300">
            <TiptapEditor
              value={editingPrompt ?? undefined}
              onChangeAction={setEditingPrompt}
              placeholder={t('promptPresetEditPromptPlaceholder')}
              hideToolbar
              compact
            />
          </div>
          {userConventionOptions.length > 0 ? (
            <UserConventionSelector
              label={t('userConventionsLabel')}
              emptyHint={t('userConventionsEmpty')}
              conventions={userConventionOptions}
              selectedIds={editingUserConventionIds}
              onToggle={handleEditingUserConventionToggle}
              maxHeightClassName="max-h-32"
            />
          ) : null}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs checkbox-primary"
                  checked={editingPlanMode}
                  onChange={(e) => setEditingPlanMode(e.target.checked)}
                />
                <span className="text-xs text-[var(--color-fg-muted)]">{t('promptPresetPlanMode')}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs checkbox-primary"
                  checked={editingIsShared}
                  onChange={(e) => setEditingIsShared(e.target.checked)}
                />
                <span className="text-xs text-[var(--color-fg-muted)]">{t('promptPresetShared')}</span>
              </label>
            </div>
            <div className="flex gap-2">
              <button type="button" className="btn btn-ghost btn-sm" onClick={handleEditCancel}>
                {tCommon('actions.cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => void handleEditSubmit(preset.id)}
                disabled={!editingName.trim() || updateMutation.isPending}
              >
                {updateMutation.isPending ? t('promptPresetSaving') : tCommon('actions.save')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        key={preset.id}
        className="group rounded-xl border border-base-300 bg-base-100 p-3 transition-colors hover:border-base-content/20"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`flex-1 min-w-0 text-left${onSelectPreset ? '' : ' cursor-default'}`}
            onClick={() => handleSelect(preset)}
            disabled={!onSelectPreset}
          >
            <span className="text-sm truncate block">{preset.name}</span>
            <span className="text-xs text-[var(--color-fg-muted)] truncate block mt-0.5">
              {getPlainText(preset.prompt as JSONContent)}
            </span>
          </button>

          {preset.isShared ? (
            <span className="shrink-0 rounded-full bg-info/15 px-1.5 py-0.5 text-[10px] font-medium text-info">
              {t('promptPresetSharedBadge')}
            </span>
          ) : null}

          {owner && canWrite ? (
            <div className="shrink-0 flex items-center gap-1.5">
              {/* Edit */}
              <button
                type="button"
                className="btn btn-ghost btn-xs hover:text-primary hover:bg-primary/10"
                onClick={() => handleEditStart(preset)}
                title="Edit"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                  />
                </svg>
              </button>
              {/* Delete */}
              <button
                type="button"
                className="btn btn-ghost btn-xs text-error hover:bg-error/10"
                onClick={() => void handleDelete(preset.id)}
                title="Delete"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      variant="bottomSheet"
      size="lg"
      backdropClassName="[backface-visibility:hidden]"
    >
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold">{t('promptPresetModalTitle')}</h3>
        <button type="button" className="btn btn-ghost btn-circle btn-sm" onClick={onClose}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {projectOptions ? (
        <div className="mb-3 space-y-1.5">
          <label className="text-xs font-medium text-[var(--color-fg-subtle)]">{t('promptPresetProjectLabel')}</label>
          <Select
            options={projectOptions}
            value={projectId}
            onChange={(value) => onProjectChange?.(value)}
            size="sm"
            fullWidth
            disabled={!onProjectChange || projectOptions.length <= 1}
          />
        </div>
      ) : null}

      {/* Search */}
      <input
        type="text"
        className="input input-bordered input-sm w-full mb-3"
        placeholder={t('promptPresetSearchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Preset list */}
      <div className="max-h-[60vh] overflow-y-auto space-y-2">
        {filtered.mine.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
              {t('promptPresetMyPresets')}
            </p>
            {filtered.mine.map(renderPresetRow)}
          </div>
        ) : null}

        {filtered.shared.length > 0 ? (
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-fg-subtle)]">
              {t('promptPresetSharedPresets')}
            </p>
            {filtered.shared.map(renderPresetRow)}
          </div>
        ) : null}

        {presetsLoadFailed ? (
          <div className="rounded-lg border border-base-300 p-3 text-sm text-[var(--color-fg-muted)]">
            {t(presetsReadDenied ? 'promptPresetNoReadPermission' : 'promptPresetLoadFailed')}
          </div>
        ) : null}

        {!presetsLoadFailed && filtered.mine.length === 0 && filtered.shared.length === 0 ? (
          <div className="rounded-lg border border-base-300 p-3 text-sm text-[var(--color-fg-muted)]">
            {t('promptPresetEmpty')}
          </div>
        ) : null}
      </div>

      {/* Save current prompt (only when used from trigger modal) */}
      {onSelectPreset && canWrite ? (
        <div className="mt-4 border-t border-base-300 pt-4">
          {isSaveOpen ? (
            <div className="rounded-xl border border-base-300 bg-base-200 p-3 space-y-2">
              <input
                type="text"
                className="input input-bordered input-sm w-full"
                placeholder={t('promptPresetNamePlaceholder')}
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                maxLength={100}
                autoFocus
              />
              {userConventionOptions.length > 0 ? (
                <UserConventionSelector
                  label={t('userConventionsLabel')}
                  emptyHint={t('userConventionsEmpty')}
                  conventions={userConventionOptions}
                  selectedIds={saveUserConventionIds}
                  onToggle={handleSaveUserConventionToggle}
                  maxHeightClassName="max-h-32"
                />
              ) : null}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs checkbox-primary"
                      checked={savePlanMode}
                      onChange={(e) => setSavePlanMode(e.target.checked)}
                    />
                    <span className="text-xs text-[var(--color-fg-muted)]">{t('promptPresetPlanMode')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs checkbox-primary"
                      checked={saveIsShared}
                      onChange={(e) => setSaveIsShared(e.target.checked)}
                    />
                    <span className="text-xs text-[var(--color-fg-muted)]">{t('promptPresetShared')}</span>
                  </label>
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => {
                      setIsSaveOpen(false);
                      resetSaveForm();
                    }}
                  >
                    {tCommon('actions.cancel')}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={() => void handleSave()}
                    disabled={!savePresetName.trim() || createMutation.isPending}
                  >
                    {createMutation.isPending ? t('promptPresetSaving') : tCommon('actions.save')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-ghost btn-sm w-full"
              onClick={() => {
                setSaveUserConventionIds(currentUserConventionIds);
                setIsSaveOpen(true);
              }}
              disabled={!hasPromptContent}
            >
              {t('promptPresetSaveCurrentExpanded')}
            </button>
          )}
        </div>
      ) : null}
    </BaseModal>
  );
};
